import { message, Modal } from 'antd';
import uuid from 'react-uuid';

/**
 * Get data from localStorage, or return defaultValue if not found.
 */
export function getLocalStorageData(key, defaultValue) {
   try {
      const data = JSON.parse(localStorage.getItem(key));
      return data !== null ? data : defaultValue;
   } catch {
      return defaultValue;
   }
}

/**
 * Set data to localStorage.
 */
export function setLocalStorageData(key, value) {
   localStorage.setItem(key, JSON.stringify(value));
}

/**
 * Remove multiple keys from localStorage.
 */
export function removeLocalStorageKeys(keys) {
   keys.forEach(key => localStorage.removeItem(key));
}

/**
 * Create assessment payload for create or update.
 * @param {Object} params - { assinfo, userId, answerSheetData, type, fileUrl }
 * @returns {Object}
 */
export function createAssessmentPayload({ assinfo, userId, answerSheetData, type = 'manual', fileUrl = '' }) {
   const isLocalhost = /localhost|[0-9]{2,3}\.[0-9]{2,3}\.[0-9]{2,3}\.[0-9]{2,3}|::1|\.local|^$/i.test(
      window.location.hostname
   );
   const o2oMode = isLocalhost ? 'offline' : 'online';
   return {
      Id: assinfo.Id || `ass_${o2oMode}_${uuid()}`,
      Title: assinfo.title,
      Term: assinfo.term,
      File_url: fileUrl,
      Question_type: type === 'pdf' ? '1' : '2',
      Created_by: userId,
      Copied_from: null,
      Question_sheet: JSON.stringify(answerSheetData),
      Level: assinfo.level,
      Subject: assinfo.subject,
      Course_topic: assinfo.course_topic ?? assinfo.Course_topic ?? '',
      Instruction: assinfo.instruction || '',
      ...(assinfo.Type ? { Type: assinfo.Type } : {})
   };
}

/**
 * Validate required fields for assessment.
 * @param {Object} params - { assinfo, answerSheetData, requireFileUrl }
 * @returns {Array} - Array of missing field names
 */
export function validateAssessmentFields({ assinfo, answerSheetData, requireFileUrl = false, requireCourseTopic = false }) {
   const requiredFields = ['title', 'level', 'term'];
   if (requireCourseTopic) {
      requiredFields.push('course_topic');
   } else {
      requiredFields.push('subject');
   }
   if (requireFileUrl) requiredFields.unshift('file_url');
   const missing = requiredFields.filter(field => !assinfo[field] || assinfo[field] === '');
   if (!answerSheetData || answerSheetData.length === 0) missing.push('answerSheetData');
   return missing;
}

/**
 * Show a modal listing missing fields.
 */
export function showValidationModal(missingFields, options = {}) {
   const { isMicrocredentials = false } = options;

   Modal.info({
      title: 'Incomplete Assessment',
      content: (
         <>
            Please complete the following before saving:
            <ul>
               {missingFields.includes('file_url') && <li>Upload questionaire</li>}
               {missingFields.includes('title') && <li>Title</li>}
               {missingFields.includes('level') && <li>Grade Level</li>}
               {missingFields.includes('subject') && <li>{isMicrocredentials ? 'Course' : 'Subject'}</li>}
               {missingFields.includes('course_topic') && <li>{isMicrocredentials ? 'Course and Topic' : 'Topic'}</li>}
               {missingFields.includes('term') && <li>Term</li>}
               {missingFields.includes('answerSheetData') && <li>Answer Keys</li>}
            </ul>
         </>
      ),
      centered: true,
   });
}

/**
 * Toggle assignment and assessment tool/sheet views.
 */
export function toggleAssignmentViews(setShowAssignment, setShowAssessmentToolsOrSheet) {
   setShowAssignment(prev => !prev);
   setShowAssessmentToolsOrSheet(prev => !prev);
}

/**
 * Check whether an axios response represents a successful (2xx) save.
 * The API may answer with an empty or non-JSON body on success, so the body
 * itself must not be used to decide whether the save succeeded.
 */
export function isSuccessfulResponse(response) {
   return !!response && response.status >= 200 && response.status < 300;
}

/**
 * Extract a human readable error message from an axios error.
 * Handles plain text bodies, JSON ReturnMessageDTO bodies and error objects.
 */
export function getSaveErrorMessage(error, fallbackMessage = 'Failed to save assessment.') {
   const data = error?.response?.data;

   if (data) {
      if (typeof data === 'string') {
         const text = data.trim();
         if (text) {
            if (text.startsWith('{')) {
               try {
                  const parsed = JSON.parse(text);
                  if (parsed?.Message) return parsed.Message;
               } catch {
                  // Not a JSON payload, fall back to the raw text below.
               }
            }
            return text;
         }
      } else if (typeof data === 'object' && data.Message) {
         return data.Message;
      }
   }

   if (error?.message) return error.message;

   return fallbackMessage;
}

// Helper: Default templates for each question type
const QUESTION_TYPE_TEMPLATES = {
   section: () => ({
      id: uuid(),
      type: "section",
      correct: [""],
   }),
   multiple_answer: (template) => ({
      id: uuid(),
      type: "multiple_answer",
      correct: ["0"],
      points: "1",
      // option_labels: [""],
      option_labels: ["A"],
      question: "",
      question_image: "",
      template,
   }),
   multiple_answer_image: (template) => ({
      id: uuid(),
      type: "multiple_answer_image",
      correct: ["0"],
      points: "1",
      option_labels: ["A"],
      image_url: [""],
      question: "",
      question_image: "",
      template,
   }),
   multiple_choice: (template) => ({
      id: uuid(),
      type: "multiple_choice",
      correct: ["0"],
      points: "1",
      // option_labels: [""],
      option_labels: ["A"],
      question: "",
      question_image: "",
      template,
   }),
   multiple_choice_image: (template) => ({
      id: uuid(),
      type: "multiple_choice_image",
      correct: ["0"],
      points: "1",
      option_labels: ["A"],
      image_url: [""],
      question: "",
      question_image: "",
      template,
   }),
   short_answer: (template) => ({
      id: uuid(),
      type: "short_answer",
      correct: [""],
      points: "1",
      question: "",
      question_image: "",
      template,
   }),
   long_answer: (template) => ({
      id: uuid(),
      type: "long_answer",
      points: "1",
      question: "",
      question_image: "",
      template,
   }),
   matching_type: (template) => ({
      id: uuid(),
      type: "matching_type",
      correct: [""],
      points: "1",
      option_labels: [["", ""]],
      question: "",
      question_image: "",
      template,
   }),
   matching_type_image: (template) => ({
      id: uuid(),
      type: "matching_type_image",
      correct: [""],
      points: "1",
      option_labels: [""],
      image_url: [["", ""]],
      question: "",
      question_image: "",
      template,
   }),
   true_or_false: (template) => ({
      id: uuid(),
      type: "true_or_false",
      correct: ["0", "0"],
      points: "1",
      option_labels: ["True", "False"],
      question: "",
      question_image: "",
      template,
   }),
};

// Helper: Clone a question for duplication
function cloneQuestion(question) {
   // Deep clone for arrays/objects
   return JSON.parse(JSON.stringify({
      ...question,
      id: uuid(),
   }));
}

/**
 * CardRequestHelper - Handles add, update, remove, duplicate, and replace operations on question sheets.
 * @param {string} method - Operation type: add, update, remove, duplicate, replace
 * @param {string} type - Question type (for add)
 * @param {object} data - Data for update
 * @param {number} idx - Index in sheet
 * @param {Array} sheet_data - The current sheet data
 * @param {object|null} template - Optional template
 * @param {boolean} has_answers - If answers exist (restricts add/remove/duplicate)
 * @returns {Array} - Updated sheet data
 */
export function CardRequestHelper(method, type, data, idx, sheet_data, template = null, has_answers = false) {
   // Deep clone the array and its objects to avoid mutating input
   const questions = sheet_data.map(q => JSON.parse(JSON.stringify(q)));

   switch (method) {
      case "replace":
         if (questions[idx]) {
            questions[idx].template = template;
         }
         break;

      case "remove":
         if (!has_answers) {
            questions.splice(idx, 1);
         } else {
            message.info("Removing of question is not allowed.");
         }
         break;

      case "update":
         if (!questions[idx]) break;
         
         const q = questions[idx];         
         // Only update correct/options for non-section/long_answer
         if (q.type !== "section" && q.type !== "long_answer") {
            q.correct = Array.isArray(data.correct) ? [...data.correct] : [];
            q.option_labels = Array.isArray(data.option_labels) ? [...data.option_labels] : [];

            if (q.type.includes("image")) {
               q.image_url = Array.isArray(data.image_url) ? [...data.image_url] : [];
            }
         } else if (q.type !== "long_answer") {
            q.correct = [data.correct?.[0] ?? ""];
         }

         q.points = data.points;
         q.question = data.question;
         q.question_image = data.question_image;
         q.question_audio = data.question_audio;
         q.question_video = data.question_video;
         break;

      case "add":
         if (!has_answers) {
            if (QUESTION_TYPE_TEMPLATES[type]) {
               questions.push(QUESTION_TYPE_TEMPLATES[type](template));
               // User feedback
               const typeMsg = {
                  section: "Added new section",
                  multiple_answer: "Added multiple answer",
                  multiple_answer_image: "Added multiple answer image",
                  multiple_choice: "Added multiple choice",
                  multiple_choice_image: "Added multiple choice image",
                  short_answer: "Added short answer",
                  long_answer: "Added essay/open-ended",
                  matching_type: "Added matching type",
                  matching_type_image: "Added matching type",
                  true_or_false: "Added true or false",
               };

               // if (typeMsg[type]) message.info(typeMsg[type]);
            }
         } else {
            message.info("Adding new question is not allowed.");
         }
         break;

      case "duplicate":
         if (!has_answers && questions[idx]) {
            const newQuestion = cloneQuestion(questions[idx]);
            // Special handling for image_url in some types
            if (newQuestion.type === "multiple_choice_image") {
               newQuestion.image_url = [""];
            }
            if (newQuestion.type === "matching_type_image") {
               newQuestion.image_url = [["", ""]];
            }
            questions.splice(idx + 1, 0, newQuestion);

            // User feedback
            const typeMsg = {
               matching_type: "Added matching type",
               matching_type_image: "Added matching type",
            };
            // if (typeMsg[newQuestion.type]) message.info(typeMsg[newQuestion.type]);
            // message.info('Copy successful');
         } else if (has_answers) {
            message.info("Adding new question is not allowed.");
         }
         break;

      default:
         break;
   }

   return questions;
}
